import { Text, Spinner, Badge, Dropdown, Option, makeStyles, tokens } from '@fluentui/react-components';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { fetchModules } from '../api/terrain.api';
import { useEntityNames } from '../hooks/useEntityNames';
import {
  fetchProductionPlan, fetchForecasts, fetchAnomalies,
  FORECAST_HORIZONS, type ForecastHorizon, type Anomaly, type AnomalySeverity, type AnomalyType,
  type ProductionPlan,
} from '../api/production.api';
import { ChartCard } from '../components/shared/ChartCard';
import { GanttView, type GanttRow } from '../components/shared/GanttView';
import { FunnelChart } from '../components/shared/FunnelChart';
import { BarLineChart, type BarLineChartPoint } from '../components/shared/charts/BarLineChart';
import { KpiCard } from '../components/shared/KpiCard';
import { DataTable, type DataTableColumn } from '../components/shared/DataTable';
import { SubTabs } from '../components/shared/SubTabs';
import { useProjectStore } from '../store/project.store';
import { useT } from '../i18n';
import { useIntlFormat } from '../hooks/useIntlFormat';
import { useRanchoStore } from '../store/rancho.store';
import { PageHeader } from '../components/shared/PageHeader';
import { PageTitle } from '../components/shared/PageTitle';

/**
 * F7 — "Producción" del portal inversionista, 6ª y última pantalla del PDF.
 * Backend F2 (PR #106): plan plan-vs-real + pronóstico determinista +
 * anomalías, ya mergeado. Filtra por el proyecto/temporada activo del TopBar
 * (F1 PR C, #107) y, dentro de él, por módulo — el plan y el forecast son
 * por módulo, no por proyecto.
 */

const useStyles = makeStyles({
  root:    { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalL, padding: tokens.spacingVerticalL },
  kpiRow:  { display: 'flex', gap: tokens.spacingHorizontalM, flexWrap: 'wrap' },
  section: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS },
  sectionTitle: { fontWeight: tokens.fontWeightSemibold, fontSize: tokens.fontSizeBase400 },
  forecastRow: { display: 'flex', gap: tokens.spacingHorizontalM, flexWrap: 'wrap' },
  anomalyRow: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: tokens.spacingHorizontalM,
    padding: '10px 14px',
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusMedium,
    borderLeft: '4px solid transparent',
  },
  anomalyInfo: { display: 'flex', flexDirection: 'column', gap: '2px' },
  anomalyTitle: { fontWeight: tokens.fontWeightSemibold, fontSize: tokens.fontSizeBase300 },
  anomalyMeta: { fontSize: tokens.fontSizeBase200, color: tokens.colorNeutralForeground3 },
  pending: {
    padding: '14px 16px',
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusMedium,
    border: `1px dashed ${tokens.colorNeutralStroke2}`,
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
  },
});

const SEVERITY_COLOR: Record<AnomalySeverity, 'informative' | 'warning' | 'danger'> = {
  BAJA: 'informative',
  MEDIA: 'warning',
  ALTA: 'warning',
  CRITICA: 'danger',
};

const SEVERITY_BORDER: Record<AnomalySeverity, string> = {
  BAJA: tokens.colorNeutralStroke2,
  MEDIA: tokens.colorPaletteYellowBorder2,
  ALTA: tokens.colorPaletteMarigoldBorder2,
  CRITICA: tokens.colorPaletteRedBorder2,
};

interface LoteProgressRow {
  loteId: string;
  cropName: string;
  currentStage: string;
  progressPct: number;
  avgDeviationDays: number | null;
  totalStages: number;
}

interface CropSummaryRow {
  cropName: string;
  loteCount: number;
  avgProgressPct: number;
}

interface ModuleForecastPoint {
  moduleCode: string;
  base: number;
}

/** Agrupa el progreso por parcela (ya calculado por `buildProgressByLote`) por
 * cultivo — promedio simple de `progressPct` entre las parcelas de ese cultivo. */
function buildCropSummary(rows: LoteProgressRow[]): CropSummaryRow[] {
  const byCrop = new Map<string, LoteProgressRow[]>();
  for (const r of rows) {
    if (!byCrop.has(r.cropName)) byCrop.set(r.cropName, []);
    byCrop.get(r.cropName)!.push(r);
  }
  return Array.from(byCrop.entries()).map(([cropName, group]) => ({
    cropName,
    loteCount: group.length,
    avgProgressPct: group.reduce((sum, r) => sum + r.progressPct, 0) / group.length,
  }));
}

/** Agrupa el plan del proyecto (todos los módulos) por parcela para la matriz
 * de progreso. % de avance = etapas con `actualEnd` / total de etapas. Etapa
 * actual = la primera sin `actualEnd` (en curso), o la última si ya cerraron
 * todas. Desviación promedio solo considera etapas que ya reportaron real. */
function buildProgressByLote(plan: ProductionPlan[], tCrop: Record<string, unknown>): LoteProgressRow[] {
  const byLote = new Map<string, ProductionPlan[]>();
  for (const p of plan) {
    if (!byLote.has(p.loteId)) byLote.set(p.loteId, []);
    byLote.get(p.loteId)!.push(p);
  }

  return Array.from(byLote.entries()).map(([loteId, stages]) => {
    const sorted = [...stages].sort((a, b) => a.seq - b.seq);
    const completed = sorted.filter(s => !!s.actualEnd);
    const current = sorted.find(s => !s.actualEnd) ?? sorted[sorted.length - 1];
    const deviations = sorted.map(s => s.deviationDays).filter((d): d is number => d !== undefined);
    const avgDeviationDays = deviations.length
      ? deviations.reduce((sum, d) => sum + d, 0) / deviations.length
      : null;

    return {
      loteId,
      cropName: sorted.find(s => s.cropName)?.cropName ?? '—',
      currentStage: current ? (tCrop[`stage${current.stageCode}`] as string ?? current.stageCode) : '—',
      progressPct: sorted.length ? (completed.length / sorted.length) * 100 : 0,
      avgDeviationDays,
      totalStages: sorted.length,
    };
  });
}

/** Gantt de TODAS las etapas de TODAS las parcelas del proyecto (no filtra por
 * módulo, a diferencia del Gantt del tab Resumen) — ordenado por lote y luego
 * por secuencia de etapa dentro del lote. */
function buildProjectGanttRows(plan: ProductionPlan[], tCrop: Record<string, unknown>, loteName: (id: string) => string): GanttRow[] {
  return [...plan]
    .sort((a, b) => (a.loteId === b.loteId ? a.seq - b.seq : a.loteId.localeCompare(b.loteId)))
    .map(p => ({
      id: p.planKey,
      label: `${loteName(p.loteId)} · ${(tCrop[`stage${p.stageCode}`] as string) ?? p.stageCode}`,
      plannedStart: p.plannedStart,
      plannedEnd: p.plannedEnd,
      actualStart: p.actualStart,
      actualEnd: p.actualEnd,
      deviationDays: p.deviationDays,
    }));
}

export function InvestorProductionPage() {
  const s = useStyles();
  const t = useT();
  const { fmtNum } = useIntlFormat();
  const { loteName } = useEntityNames();

  const activeProjectId = useProjectStore(st => st.activeProjectId);
  const activeRanchoId = useRanchoStore(st => st.activeRanchoId);
  const [selectedModuleId, setSelectedModuleId] = useState<string | null>(null);
  const [subtab, setSubtab] = useState<'resumen' | 'progreso'>('resumen');

  const { data: modules = [], isLoading: modulesLoading } = useQuery({
    queryKey: ['investor-production-modules', activeProjectId, activeRanchoId],
    queryFn: () => fetchModules({ projectId: activeProjectId!, ranchoId: activeRanchoId }),
    enabled: !!activeProjectId,
  });

  // Al cambiar de proyecto (o cargar la lista), selecciona el primer módulo
  // automáticamente — el inversionista no debe teclear un moduleId a mano.
  useEffect(() => {
    if (modules.length > 0 && !modules.some(m => m.moduleId === selectedModuleId)) {
      setSelectedModuleId(modules[0].moduleId);
    } else if (modules.length === 0) {
      setSelectedModuleId(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modules]);

  const { data: plan = [], isLoading: planLoading } = useQuery({
    queryKey: ['investor-production-plan', activeProjectId, selectedModuleId, activeRanchoId],
    queryFn: () => fetchProductionPlan(activeProjectId!, selectedModuleId!, activeRanchoId),
    enabled: !!activeProjectId && !!selectedModuleId,
  });

  const forecast7 = useQuery({
    queryKey: ['investor-production-forecast', selectedModuleId, 7],
    queryFn: () => fetchForecasts(selectedModuleId!, { horizonDays: 7, limit: 1 }),
    enabled: !!selectedModuleId,
  });
  const forecast15 = useQuery({
    queryKey: ['investor-production-forecast', selectedModuleId, 15],
    queryFn: () => fetchForecasts(selectedModuleId!, { horizonDays: 15, limit: 1 }),
    enabled: !!selectedModuleId,
  });
  const forecast60 = useQuery({
    queryKey: ['investor-production-forecast', selectedModuleId, 60],
    queryFn: () => fetchForecasts(selectedModuleId!, { horizonDays: 60, limit: 1 }),
    enabled: !!selectedModuleId,
  });
  const forecastQueries = [forecast7, forecast15, forecast60];

  const { data: anomalies = [], isLoading: anomaliesLoading } = useQuery({
    queryKey: ['investor-production-anomalies', activeProjectId, activeRanchoId],
    queryFn: () => fetchAnomalies({ projectId: activeProjectId!, status: 'ABIERTA', ranchoId: activeRanchoId }),
    enabled: !!activeProjectId,
  });

  // Producción-Progreso (Fase C) — TODAS las etapas de TODOS los módulos del
  // proyecto (sin moduleId), a diferencia de `plan` arriba que es por módulo.
  const { data: projectPlan = [], isLoading: projectPlanLoading } = useQuery({
    queryKey: ['investor-production-progress', activeProjectId, activeRanchoId],
    queryFn: () => fetchProductionPlan(activeProjectId!, undefined, activeRanchoId),
    enabled: !!activeProjectId && subtab === 'progreso',
  });

  const { data: progressForecastRows = [], isLoading: progressForecastLoading } = useQuery({
    queryKey: ['investor-production-progress-forecast', modules.map(m => m.moduleId).join(',')],
    queryFn: async (): Promise<ModuleForecastPoint[]> => Promise.all(modules.map(async m => {
      const items = await fetchForecasts(m.moduleId, { horizonDays: 15, limit: 1 });
      return { moduleCode: m.moduleCode, base: items[0]?.scenarios.base ?? 0 };
    })),
    enabled: modules.length > 0 && subtab === 'progreso',
  });

  const sortedPlan = [...plan].sort((a, b) => a.seq - b.seq);
  const progressRows = buildProgressByLote(projectPlan, t.crop);
  const cropSummaryRows = buildCropSummary(progressRows);
  const projectGanttRows = buildProjectGanttRows(projectPlan, t.crop, loteName);

  const maxForecastBase = Math.max(1, ...progressForecastRows.map(r => r.base));
  const forecastChartData: BarLineChartPoint[] = progressForecastRows.map(r => ({
    label: r.moduleCode,
    barHeightPct: (r.base / maxForecastBase) * 100,
  }));

  const ganttRows: GanttRow[] = sortedPlan.map(p => ({
    id: p.planKey,
    label: t.crop[`stage${p.stageCode}` as keyof typeof t.crop] as string,
    plannedStart: p.plannedStart,
    plannedEnd: p.plannedEnd,
    actualStart: p.actualStart,
    actualEnd: p.actualEnd,
    deviationDays: p.deviationDays,
  }));

  const funnelData = sortedPlan.map(p => ({
    name: t.crop[`stage${p.stageCode}` as keyof typeof t.crop] as string,
    value: p.yieldCurvePct,
  }));

  function anomalyTypeLabel(tipo: AnomalyType): string {
    return t.investorProduction[`anomalyType${tipo}` as keyof typeof t.investorProduction] as string;
  }

  function anomalySeverityLabel(sev: AnomalySeverity): string {
    return t.investorProduction[`severity${sev}` as keyof typeof t.investorProduction] as string;
  }

  function forecastHorizonLabel(h: ForecastHorizon): string {
    return t.investorProduction[`horizon${h}` as keyof typeof t.investorProduction] as string;
  }

  if (!activeProjectId) {
    return (
      <div className={s.root}>
        <PageTitle>{t.investorProduction.title}</PageTitle>
        <div className={s.pending}>{t.investorProduction.noProjectSelected}</div>
      </div>
    );
  }

  return (
    <div className={s.root}>
      <PageHeader title={t.investorProduction.title} subtitle={t.investorProduction.subtitle} />

      <SubTabs
        selected={subtab}
        onSelect={v => setSubtab(v as 'resumen' | 'progreso')}
        tabs={[
          { value: 'resumen',  label: t.investorProduction.tabResumen },
          { value: 'progreso', label: t.investorProduction.tabProgreso },
        ]}
      />

      {subtab === 'progreso' && (
        <div className={s.section}>
          <span className={s.sectionTitle}>{t.investorProduction.progressTitle}</span>
          <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>{t.investorProduction.progressSubtitle}</Text>
          <DataTable<LoteProgressRow>
            isLoading={projectPlanLoading}
            emptyMessage={t.investorProduction.noPlan}
            rows={progressRows}
            rowKey={r => r.loteId}
            columns={[
              { key: 'loteId', header: t.investorProduction.progressColLote, width: 1.2 },
              { key: 'cropName', header: t.investorProduction.progressColCrop },
              { key: 'currentStage', header: t.investorProduction.progressColCurrentStage, width: 1.4 },
              { key: 'progressPct', header: t.investorProduction.progressColPct, align: 'right', render: r => `${r.progressPct.toFixed(0)}%` },
              {
                key: 'avgDeviationDays', header: t.investorProduction.progressColDeviation, align: 'right',
                render: r => r.avgDeviationDays === null ? '—' : `${r.avgDeviationDays > 0 ? '+' : ''}${r.avgDeviationDays.toFixed(1)}`,
              },
            ] as DataTableColumn<LoteProgressRow>[]}
          />
          <span className={s.sectionTitle}>{t.investorProduction.progressCropTitle}</span>
          <div className={s.kpiRow}>
            {cropSummaryRows.map(c => (
              <KpiCard
                key={c.cropName}
                label={c.cropName}
                value={`${c.avgProgressPct.toFixed(0)}%`}
                sub={`${c.loteCount} ${t.investorProduction.progressCropLoteCount}`}
              />
            ))}
          </div>

          <span className={s.sectionTitle}>{t.investorProduction.progressGanttTitle}</span>
          <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>{t.investorProduction.progressGanttSubtitle}</Text>
          <ChartCard
            title={t.investorProduction.progressGanttTitle}
            isLoading={projectPlanLoading}
            isEmpty={!projectPlanLoading && projectGanttRows.length === 0}
            emptyMessage={t.investorProduction.noPlan}
          >
            <GanttView
              rows={projectGanttRows}
              plannedLabel={t.investorProduction.ganttPlanned}
              actualLabel={t.investorProduction.ganttActual}
              onTimeLabel={t.investorProduction.ganttOnTime}
              lateLabel={t.investorProduction.ganttLate}
              earlyLabel={t.investorProduction.ganttEarly}
            />
          </ChartCard>

          <span className={s.sectionTitle}>{t.investorProduction.progressForecastTitle}</span>
          <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>{t.investorProduction.progressForecastSubtitle}</Text>
          <ChartCard
            title={t.investorProduction.progressForecastTitle}
            isLoading={progressForecastLoading}
            isEmpty={!progressForecastLoading && forecastChartData.length === 0}
            emptyMessage={t.investorProduction.noForecast}
          >
            <BarLineChart data={forecastChartData} />
          </ChartCard>
        </div>
      )}

      {subtab === 'resumen' && <>
      {modulesLoading && <Spinner size="small" label={t.investorProduction.loadingModules} />}
      {!modulesLoading && modules.length === 0 && (
        <div className={s.pending}>
          <div style={{ fontWeight: 600, marginBottom: 4 }}>{t.investorProduction.noModules}</div>
          <div>{t.investorProduction.noModulesHint}</div>
        </div>
      )}
      {modules.length > 0 && (
        <Dropdown
          size="small"
          aria-label={t.investorProduction.moduleSelector}
          placeholder={t.investorProduction.moduleSelector}
          value={modules.find(m => m.moduleId === selectedModuleId)?.moduleCode ?? ''}
          selectedOptions={selectedModuleId ? [selectedModuleId] : []}
          onOptionSelect={(_, data) => setSelectedModuleId(data.optionValue ?? null)}
          style={{ minWidth: '220px', maxWidth: '280px' }}
        >
          {modules.map(m => {
            const text = `${m.moduleCode}${m.cropName ? ` — ${m.cropName}` : ''}`;
            return <Option key={m.moduleId} value={m.moduleId} text={text}>{text}</Option>;
          })}
        </Dropdown>
      )}

      {/* Gantt plan vs. real */}
      <div className={s.section}>
        <span className={s.sectionTitle}>{t.investorProduction.ganttTitle}</span>
        <ChartCard
          title={t.investorProduction.ganttTitle}
          subtitle={t.investorProduction.ganttSubtitle}
          isLoading={planLoading}
          loadingLabel={t.investorProduction.loadingPlan}
          isEmpty={!planLoading && ganttRows.length === 0}
          emptyMessage={t.investorProduction.noPlan}
        >
          <GanttView
            rows={ganttRows}
            plannedLabel={t.investorProduction.ganttPlanned}
            actualLabel={t.investorProduction.ganttActual}
            onTimeLabel={t.investorProduction.ganttOnTime}
            lateLabel={t.investorProduction.ganttLate}
            earlyLabel={t.investorProduction.ganttEarly}
          />
        </ChartCard>
      </div>

      {/* Curva de rendimiento acumulado */}
      <div className={s.section}>
        <ChartCard
          title={t.investorProduction.funnelTitle}
          subtitle={t.investorProduction.funnelSubtitle}
          isLoading={planLoading}
          loadingLabel={t.investorProduction.loadingPlan}
          isEmpty={!planLoading && funnelData.length === 0}
          emptyMessage={t.investorProduction.noPlan}
        >
          <FunnelChart data={funnelData} />
        </ChartCard>
      </div>

      {/* Forecast — 3 horizontes, kg incrementales de la ventana */}
      <div className={s.section}>
        <span className={s.sectionTitle}>{t.investorProduction.forecastTitle}</span>
        <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>{t.investorProduction.forecastDisclaimer}</Text>
        <div className={s.forecastRow}>
          {FORECAST_HORIZONS.map((horizonDays, idx) => {
            const query = forecastQueries[idx];
            const forecast = query.data?.[0];
            return (
              <ChartCard
                key={horizonDays}
                title={forecastHorizonLabel(horizonDays)}
                subtitle={forecast ? `${t.investorProduction.confidence}: ${forecast.confidencePct.toFixed(0)}%` : undefined}
                isLoading={query.isLoading}
                loadingLabel={t.investorProduction.loadingForecast}
                isEmpty={!query.isLoading && !forecast}
                emptyMessage={t.investorProduction.noForecast}
              >
                {forecast && (
                  <div className={s.kpiRow}>
                    <KpiCard label={t.investorProduction.scenarioConservador} value={`${fmtNum(forecast.scenarios.conservador)} ${forecast.unit}`} />
                    <KpiCard label={t.investorProduction.scenarioBase} value={`${fmtNum(forecast.scenarios.base)} ${forecast.unit}`} />
                    <KpiCard label={t.investorProduction.scenarioIdeal} value={`${fmtNum(forecast.scenarios.ideal)} ${forecast.unit}`} />
                  </div>
                )}
              </ChartCard>
            );
          })}
        </div>
      </div>

      {/* Anomalías abiertas */}
      <div className={s.section}>
        <span className={s.sectionTitle}>{t.investorProduction.anomaliesTitle}</span>
        {anomaliesLoading && <Spinner size="small" label={t.investorProduction.loadingAnomalies} />}
        {!anomaliesLoading && anomalies.length === 0 && <Text size={200}>{t.investorProduction.noAnomalies}</Text>}
        {anomalies.length > 0 && anomalies.map((a: Anomaly) => (
          <div key={`${a.projectId}#${a.anomalyKey}`} className={s.anomalyRow} style={{ borderLeftColor: SEVERITY_BORDER[a.severity] }}>
            <div className={s.anomalyInfo}>
              <span className={s.anomalyTitle}>{a.title}</span>
              <span className={s.anomalyMeta}>
                {anomalyTypeLabel(a.tipo)}
                {a.responsibleUserId ? ` · ${t.investorProduction.responsible}: ${a.responsibleUserId}` : ''}
              </span>
            </div>
            <Badge appearance="tint" color={SEVERITY_COLOR[a.severity]}>{anomalySeverityLabel(a.severity)}</Badge>
          </div>
        ))}
      </div>
      </>}
    </div>
  );
}
